import Papa from 'papaparse';

export interface CSVOpportunityData {
  OpportunityID: number;
  Name: string;
  SpeedUpPermitting: number;
  IncreaseStaffProductivity: number;
  SimplifyApplicationExperience: number;
  EnhanceEquitableAccessToServices: number;
  StrengthenInternalVisibilityDecisionMaking: number;
  IncreaseProcessTransparency: number;
  BoostFinancialSustainability: number;
  CostAndEffort: number;
  Risk: number;
  Complexity: number;
}

export interface CSVOpportunityMatrixV2 {
  'Opportunity Name': string;
  Description: string;
  'Why It Matters': string;
  ProductURL?: string;
  'Weight Dimension'?: string;
  SpeedUpPermitting?: number;
  IncreaseStaffProductivity?: number;
  SimplifyApplicationExperience?: number;
  EnhanceEquitableAccessToServices?: number;
  StrengthenInternalVisibilityDecisionMaking?: number;
  IncreaseProcessTransparency?: number;
  BoostFinancialSustainability?: number;
  CostAndEffort?: number;
  Risk?: number;
  Complexity?: number;
}

/**
 * Loads opportunity data from the opportunity matrix v2 CSV file
 * This is the SOLE source of truth for all opportunity data
 */
export async function loadOpportunitiesFromCSV(): Promise<CSVOpportunityData[]> {
  try {
    console.log('Loading opportunities from matrix v2 CSV (sole source of truth)...');
    const response = await fetch('/opportunity matrix v2.csv');
    const csvText = await response.text();
    
    const result = Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      transform: (value) => value.trim(),
      complete: (results) => {
        console.log('Papa Parse completed with', results.data.length, 'rows');
        console.log('Papa Parse errors:', results.errors);
      },
      // Add these options to better handle multi-line fields
      dynamicTyping: false,
      fastMode: false
    });

    if (result.errors.length > 0) {
      console.error('CSV parsing errors:', result.errors);
      throw new Error('Failed to parse CSV file');
    }

    const matrixData = result.data as CSVOpportunityMatrixV2[];
    console.log('Raw CSV data length:', matrixData.length);
    console.log('Raw CSV data:', matrixData.map((row, index) => ({ index, name: row['Opportunity Name'], hasName: !!row['Opportunity Name'] })));
    
    const opportunities: CSVOpportunityData[] = [];
    const processedNames = new Set<string>();
    
    matrixData.forEach((row, index) => {
      if (row['Opportunity Name']) {
        const opportunityName = row['Opportunity Name'].trim();
        
        // Skip if we've already processed this opportunity name
        if (processedNames.has(opportunityName)) {
          console.log(`Skipping duplicate opportunity: "${opportunityName}"`);
          return;
        }
        
        processedNames.add(opportunityName);
        console.log(`Processing row ${index + 1}: "${opportunityName}"`);
        
        const opportunity: CSVOpportunityData = {
          OpportunityID: 100 + index + 1,
          Name: opportunityName,
          SpeedUpPermitting: parseInt(row.SpeedUpPermitting?.toString() || '0', 10),
          IncreaseStaffProductivity: parseInt(row.IncreaseStaffProductivity?.toString() || '0', 10),
          SimplifyApplicationExperience: parseInt(row.SimplifyApplicationExperience?.toString() || '0', 10),
          EnhanceEquitableAccessToServices: parseInt(row.EnhanceEquitableAccessToServices?.toString() || '0', 10),
          StrengthenInternalVisibilityDecisionMaking: parseInt(row.StrengthenInternalVisibilityDecisionMaking?.toString() || '0', 10),
          IncreaseProcessTransparency: parseInt(row.IncreaseProcessTransparency?.toString() || '0', 10),
          BoostFinancialSustainability: parseInt(row.BoostFinancialSustainability?.toString() || '0', 10),
          CostAndEffort: parseInt(row.CostAndEffort?.toString() || '0', 10),
          Risk: parseInt(row.Risk?.toString() || '0', 10),
          Complexity: parseInt(row.Complexity?.toString() || '0', 10)
        };

        opportunities.push(opportunity);
      }
    });

    console.log(`Successfully loaded ${opportunities.length} opportunities from matrix v2 CSV`);
    console.log('Opportunity names:', opportunities.map(opp => opp.Name));
    return opportunities;
  } catch (error) {
    console.error('Error loading opportunities from matrix v2 CSV:', error);
    throw error;
  }
}

/**
 * Loads opportunity definitions from the opportunity matrix v2 CSV file
 * Returns separate description, "why it matters", and product URL fields
 * This is the SOLE source of truth for all opportunity definitions
 */
export async function loadOpportunityDefinitionsFromCSV(): Promise<Record<string, { description: string; whyItMatters: string; productURL: string }>> {
  try {
    console.log('Loading opportunity definitions from matrix v2 CSV (sole source of truth)...');
    const response = await fetch('/opportunity matrix v2.csv');
    const csvText = await response.text();
    
    const result = Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      transform: (value) => value.trim()
    });

    if (result.errors.length > 0) {
      console.error('CSV parsing errors:', result.errors);
      throw new Error('Failed to parse CSV file');
    }

    const definitions: Record<string, { description: string; whyItMatters: string; productURL: string }> = {};
    
    result.data.forEach((row: CSVOpportunityMatrixV2) => {
      const name = row['Opportunity Name'];
      const description = row['Description'];
      const whyItMatters = row['Why It Matters'];
      const productURL = row['ProductURL'];
      
      if (name && description) {
        definitions[name] = {
          description: description,
          whyItMatters: whyItMatters || '',
          productURL: productURL || ''
        };
      }
    });

    console.log(`Successfully loaded ${Object.keys(definitions).length} definitions from matrix v2 CSV`);
    return definitions;
  } catch (error) {
    console.error('Error loading opportunity definitions from matrix v2 CSV:', error);
    throw error;
  }
}

/**
 * Loads both opportunities and definitions from the matrix v2 CSV file
 * This is the SOLE source of truth for all opportunity data
 */
export async function loadOpportunityDataFromCSV() {
  try {
    console.log('Loading opportunity data from matrix v2 CSV (sole source of truth)...');
    const [opportunities, definitions] = await Promise.all([
      loadOpportunitiesFromCSV(),
      loadOpportunityDefinitionsFromCSV()
    ]);

    console.log('Successfully loaded opportunity data from matrix v2 CSV');
    return {
      opportunities,
      definitions
    };
  } catch (error) {
    console.error('Error loading opportunity data from matrix v2 CSV:', error);
    throw error;
  }
}
